import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Project, Database } from '@claude-code-api/shared';
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@/components/ui';
import { toast } from 'sonner';
import { Plus, Database as DatabaseIcon } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { DatabaseList } from '@/components/features/databases/DatabaseList';
import type { DatabaseInfo } from '@/components/features/databases/DatabaseList';

export default function DatabasesPage() {
  const { t } = useTranslation(['databases', 'common']);
  const [databases, setDatabases] = useState<DatabaseInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DatabaseInfo | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [createName, setCreateName] = useState('');
  const [createDbName, setCreateDbName] = useState('');
  const [createUsername, setCreateUsername] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [standaloneRes, projectsRes] = await Promise.all([
        fetch('/api/databases', { credentials: 'include' }),
        fetch('/api/projects', { credentials: 'include' }),
      ]);
      const all: DatabaseInfo[] = [];
      if (standaloneRes.ok) {
        const data = await standaloneRes.json();
        (data.data as Database[]).forEach((db) =>
          all.push({
            id: db.id,
            name: db.name,
            type: 'standalone',
            credentials: null,
            tables: [],
            loadingCredentials: false,
            loadingTables: false,
          }),
        );
      }
      if (projectsRes.ok) {
        const data = await projectsRes.json();
        (data.data as Project[])
          .filter((p) => p.hasDatabase)
          .forEach((p) =>
            all.push({
              id: p.id,
              name: p.name,
              type: 'project',
              projectId: p.id,
              projectName: p.name,
              credentials: null,
              tables: [],
              loadingCredentials: false,
              loadingTables: false,
            }),
          );
      }
      all.sort((a, b) =>
        a.type !== b.type
          ? a.type === 'standalone'
            ? -1
            : 1
          : a.name.localeCompare(b.name),
      );
      setDatabases(all);
    } catch {
      toast.error(t('databases:errors.loadDatabases'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateDatabase = useCallback(
    (id: string, updates: Partial<DatabaseInfo>) => {
      setDatabases((prev) =>
        prev.map((d) => (d.id === id ? { ...d, ...updates } : d)),
      );
    },
    [],
  );

  const handleCreate = async () => {
    if (!createName.trim()) {
      setCreateError(t('databases:errors.displayNameRequired'));
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const body: Record<string, string> = { name: createName.trim() };
      if (createDbName.trim()) body.dbName = createDbName.trim();
      if (createUsername.trim()) body.dbUsername = createUsername.trim();
      if (createPassword.trim()) body.dbPassword = createPassword.trim();
      const res = await fetch('/api/databases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const result = await res.json();
      if (res.ok) {
        setCreateOpen(false);
        resetCreateForm();
        toast.success(t('databases:messages.databaseCreated'));
        fetchAll();
      } else {
        setCreateError(result.error || t('databases:errors.createDatabase'));
      }
    } catch {
      setCreateError(t('databases:errors.createDatabase'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/databases/${deleteTarget.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setDeleteTarget(null);
        toast.success(t('databases:messages.databaseDeleted'));
        fetchAll();
      } else {
        toast.error(t('databases:errors.deleteDatabase'));
      }
    } catch {
      toast.error(t('databases:errors.deleteDatabase'));
    } finally {
      setDeleting(false);
    }
  };

  const resetCreateForm = () => {
    setCreateName('');
    setCreateDbName('');
    setCreateUsername('');
    setCreatePassword('');
    setCreateError(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('databases:title')}
        description={t('databases:description')}
        action={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('databases:actions.create')}
          </Button>
        }
      />

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      ) : databases.length === 0 ? (
        <EmptyState
          icon={DatabaseIcon}
          title={t('databases:empty.title')}
          description={t('databases:empty.description')}
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('databases:actions.create')}
            </Button>
          }
        />
      ) : (
        <DatabaseList
          databases={databases}
          onDelete={setDeleteTarget}
          onUpdateDatabase={updateDatabase}
        />
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetCreateForm();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('databases:createDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('databases:createDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {createError && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                {createError}
              </div>
            )}
            <div>
              <Label className="mb-1 block">
                {t('databases:createDialog.displayName')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder={t('databases:createDialog.displayNamePlaceholder')}
                autoFocus
              />
            </div>
            <div>
              <Label className="mb-1 block">
                {t('databases:createDialog.databaseName')}
              </Label>
              <Input
                value={createDbName}
                onChange={(e) => setCreateDbName(e.target.value)}
                placeholder={t(
                  'databases:createDialog.databaseNamePlaceholder',
                )}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t('databases:createDialog.optionalAutoGenerated')}
              </p>
            </div>
            <div>
              <Label className="mb-1 block">
                {t('databases:createDialog.username')}
              </Label>
              <Input
                value={createUsername}
                onChange={(e) => setCreateUsername(e.target.value)}
                placeholder={t('databases:createDialog.usernamePlaceholder')}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t('databases:createDialog.optionalAutoGenerated')}
              </p>
            </div>
            <div>
              <Label className="mb-1 block">
                {t('databases:createDialog.password')}
              </Label>
              <Input
                type="password"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                placeholder={t('databases:createDialog.passwordPlaceholder')}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t('databases:createDialog.optionalAutoGenerated')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('common:cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !createName.trim()}
            >
              {creating
                ? t('databases:createDialog.creating')
                : t('databases:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t('databases:deleteDialog.title')}
        description={t('databases:deleteDialog.description', {
          name: deleteTarget?.name || '',
        })}
        confirmLabel={t('databases:actions.delete')}
        cancelLabel={t('common:cancel')}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
}
